import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useBadgeAnalytics } from '../../../hooks/useAnalytics';
import MetricCard from '../core/MetricCard';
import AreaChart from '../../ui/AreaChart';
import BarChart from '../../ui/BarChart';
import PieChart from '../../ui/PieChart';
import DataTable from '../../ui/DataTable';
import CollapsibleSection from '../../ui/CollapsibleSection';
import { transformToChartData, transformToChartDataAsync } from '../../../services/chartService';
import { formatPercent, safeFormatFixed } from '../../../utils/format';
import { useFormatting } from '../../../hooks/useFormatting';
import { FILTER_PERIODS } from '../../../constants';

/**
 * Badge Metrics Component 🆕
 * Displays badge system-related analytics and statistics
 * @param {Object} props - Component props
 * @param {string} props.period - Time period for metrics
 * @param {boolean} [props.compact] - Whether to display in compact mode
 */
const BadgeMetrics = ({ period, compact = false }) => {
  const { data: badgeData, loading, error } = useBadgeAnalytics(period);

  // Generate chart data for key metrics
  const [badgeClaimsChart, setBadgeClaimsChart] = useState(null);
  const [previousBadgeData, setPreviousBadgeData] = useState(null);

  // Fetch previous period data for accurate comparison
  useEffect(() => {
    const fetchPreviousPeriodData = async () => {
      try {
        const prevPeriodMap = {
          [FILTER_PERIODS.DAY]: FILTER_PERIODS.DAY,
          [FILTER_PERIODS.WEEK]: FILTER_PERIODS.WEEK,
          [FILTER_PERIODS.MONTH]: FILTER_PERIODS.MONTH,
          [FILTER_PERIODS.YEAR]: FILTER_PERIODS.YEAR,
          [FILTER_PERIODS.ALL]: FILTER_PERIODS.YEAR
        };
        
        const prevPeriod = prevPeriodMap[period] || FILTER_PERIODS.DAY;
        const { getBadgeAnalytics } = await import('../../../services/firebaseService');
        const prevBadge = await getBadgeAnalytics(prevPeriod).catch(() => null);
        setPreviousBadgeData(prevBadge);
      } catch (error) {
        setPreviousBadgeData(null);
      }
    };

    if (badgeData) {
      fetchPreviousPeriodData();
    }
  }, [period, badgeData]);

  useEffect(() => {
    if (!badgeData) {
      setBadgeClaimsChart(null);
      return;
    }

    const currentClaims = badgeData.newBadgeClaims || 0;
    const previousClaims = previousBadgeData?.newBadgeClaims || currentClaims * 0.9;
    
    Promise.all([
      transformToChartDataAsync(
        { newBadgeClaims: currentClaims },
        { newBadgeClaims: previousClaims },
        'newBadgeClaims',
        period,
        {
          collectionName: 'users',
          valueExtractor: () => 1 // Count badge claims (would need subcollection access)
        }
      )
    ]).then(([claimsChart]) => {
      setBadgeClaimsChart(claimsChart);
    }).catch(() => {
      setBadgeClaimsChart(transformToChartData(
        { newBadgeClaims: currentClaims },
        { newBadgeClaims: previousClaims },
        'newBadgeClaims',
        period
      ));
    });
  }, [badgeData, previousBadgeData, period]);

  // Get formatting functions from hook
  const { formatNumber } = useFormatting();

  // Memoize badge type chart data
  const badgeTypeChartData = useMemo(() => {
    if (!badgeData?.badgeClaimsByType) return null;
    const types = badgeData.badgeClaimsByType;
    return [
      { label: 'Achievement', value: types.achievement || 0 },
      { label: 'Event', value: types.event || 0 },
      { label: 'Community', value: types.community || 0 }
    ].filter(item => item.value > 0);
  }, [badgeData]);

  // Prepare table data for top badges
  const topBadgesColumns = [
    { key: 'rank', label: 'Rank' },
    { key: 'badgeId', label: 'Badge ID' },
    { 
      key: 'count', 
      label: 'Claims',
      render: (value) => typeof value === 'number' ? formatNumber(value) : value
    }
  ];

  const topBadgesData = useMemo(() => {
    if (!badgeData?.topBadges || badgeData.topBadges.length === 0) return [];
    return badgeData.topBadges.map((badge, index) => ({
      id: badge.badgeId || index,
      rank: index + 1,
      badgeId: badge.badgeId || 'Unknown',
      count: badge.count || 0
    }));
  }, [badgeData?.topBadges, formatNumber]);

  // Error state
  if (error) {
    return (
      <div className="badge-metrics error">
        <h3>Error loading badge metrics</h3>
        <p>Please try refreshing the page</p>
      </div>
    );
  }

  // Loading state
  if (loading && !badgeData) {
    return (
      <div className="badge-metrics">
        <h2 className="section-title">
          <i className="bi bi-award icon-brand" style={{ marginRight: 8 }}></i> Badge System Metrics
        </h2>
        <div className="metric-loading">
          <div className="loading-spinner" />
          <p>Loading badge data...</p>
        </div>
      </div>
    );
  }

  // Normalize data for safe access
  const normalized = {
    totalBadgeClaims: badgeData?.totalBadgeClaims || 0,
    newBadgeClaims: badgeData?.newBadgeClaims || 0,
    usersWithBadges: badgeData?.usersWithBadges || 0,
    usersWithActiveBadge: badgeData?.usersWithActiveBadge || 0,
    averageBadgesPerUser: badgeData?.averageBadgesPerUser || 0,
    activeBadgeRate: badgeData?.activeBadgeRate || 0,
    badgeAdoptionRate: badgeData?.badgeAdoptionRate || 0,
    totalPointsFromBadges: badgeData?.totalPointsFromBadges || 0,
    badgeClaimsByType: badgeData?.badgeClaimsByType || { achievement: 0, event: 0, community: 0 }
  };

  // Check if we have any data
  const hasData = normalized.totalBadgeClaims > 0 || normalized.usersWithBadges > 0 || normalized.newBadgeClaims > 0;

  // No data state
  if (!hasData && !loading) {
    return (
      <div className="badge-metrics">
        <h2 className="section-title">
          <i className="bi bi-award icon-brand" style={{ marginRight: 8 }}></i> Badge System Metrics
        </h2>
        <div className="no-data-message">
          <p>No badge data available for the selected period.</p>
          <p className="no-data-hint">Badge claims will appear here once users earn badges.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="badge-metrics">
      <h2 className="section-title">
        <i className="bi bi-award icon-brand" style={{ marginRight: 8 }}></i> Badge System Metrics
      </h2>
      
      {/* Key Metrics */}
      <div className="metrics-grid-wrapper">
        <div className="metrics-grid">
          <div className="metric-group">
            <h3 className="group-title">Badge Metrics</h3>
            <div className="metric-cards">
              <MetricCard
                title="Total Badge Claims"
                value={normalized.totalBadgeClaims}
                formatType="number"
                loading={loading}
                icon={<i className="bi bi-award icon-brand"></i>}
                color="blue"
                subtitle="all time"
              />
              <MetricCard
                title="New Claims"
                value={normalized.newBadgeClaims}
                formatType="number"
                loading={loading}
                icon={<i className="bi bi-plus-circle icon-brand"></i>}
                color="green"
                subtitle={`${period} period`}
              />
              <MetricCard
                title="Users with Badges"
                value={normalized.usersWithBadges}
                formatType="number"
                loading={loading}
                icon={<i className="bi bi-people icon-brand"></i>}
                color="purple"
                subtitle={formatPercent(normalized.badgeAdoptionRate, 1)}
              />
              <MetricCard
                title="Active Badge Users"
                value={normalized.usersWithActiveBadge}
                formatType="number"
                loading={loading}
                icon={<i className="bi bi-star-fill icon-brand"></i>}
                color="orange"
                subtitle={formatPercent(normalized.activeBadgeRate, 1)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Badge Type Distribution Chart */}
      {badgeTypeChartData && (
        <div className={compact ? "chart-container-compact" : "chart-container"}>
          <PieChart
            data={badgeTypeChartData}
            label="Badge Type Distribution"
            formatType="number"
            showPercentages={true}
            innerRadius={0.5}
          />
        </div>
      )}

      {/* All Other Metrics in Show More */}
      <CollapsibleSection
        title="Additional Badge Metrics"
        initialVisible={2}
        defaultExpanded={false}
      >
        {[
          /* Badge Statistics */
          <div key="stats" className="metric-group">
            <h3 className="group-title">Badge Statistics</h3>
            <div className="metric-cards">
              <MetricCard
                title="Avg Badges/User"
                value={safeFormatFixed(normalized.averageBadgesPerUser, 1)}
                loading={loading}
                icon={<i className="bi bi-bar-chart icon-brand"></i>}
                color="cyan"
                subtitle="per user"
              />
              <MetricCard
                title="Points from Badges"
                value={normalized.totalPointsFromBadges}
                formatType="number"
                loading={loading}
                icon={<i className="bi bi-star icon-brand"></i>}
                color="yellow"
                subtitle="total points awarded"
              />
            </div>
          </div>,

          // Charts in show more
          badgeClaimsChart && (
            <div key="claims-chart" className={compact ? "chart-container-compact" : "chart-container"}>
              <AreaChart
                data={badgeClaimsChart.data}
                comparisonData={badgeClaimsChart.comparisonData}
                label="New Badge Claims Over Time"
                formatType="number"
                color="green"
              />
            </div>
          ),

          // Top Badges Table
          topBadgesData.length > 0 && (
            <div key="top-badges-table">
              <DataTable
                title="Top Badges by Claims"
                data={topBadgesData}
                columns={topBadgesColumns}
                loading={loading}
                searchable={true}
                sortable={true}
              />
            </div>
          )
        ].filter(Boolean)}
      </CollapsibleSection>
    </div>
  );
};

BadgeMetrics.propTypes = {
  period: PropTypes.string.isRequired,
  compact: PropTypes.bool
};

BadgeMetrics.defaultProps = {
  compact: false
};

export default React.memo(BadgeMetrics);

